<script lang="ts">
    import type { ComponentType } from 'svelte';
    import { draggable, embedInParent } from 'portlet/common/utils/directives';
    import { noop } from 'portlet/common/utils';
    import { setModalContext } from 'portlet/common/contexts';
    import type { VoidCallback, Open, Close } from './types';

    interface Props {
        closeButton?: boolean;
        closeOnEsc?: boolean;
        styleWindow?: Record<string, string | number>;
        styleContent?: Record<string, string | number>;
        styleCloseButton?: Record<string, string | number>;
        title?: string;
    }

    let {
        closeButton = true,
        closeOnEsc = true,
        styleWindow = {},
        styleContent = {},
        styleCloseButton = {},
        title = 'This is a dialog',
    }: Props = $props();

    const defaultState = {
        closeButton,
        closeOnEsc,
        styleWindow,
        styleContent,
        styleCloseButton,
        title,
    };

    let cmpState = $state({ ...defaultState });
    let ActiveComponent = $state<ComponentType | null>(null);
    let componentProps = $state<Record<string, any> | null>(null);
    let modalWindow = $state<HTMLElement | null>(null);

    let cssWindow = $derived(toCssString(cmpState.styleWindow));
    let cssContent = $derived(toCssString(cmpState.styleContent));
    let cssCloseButton = $derived(toCssString(cmpState.styleCloseButton));

    let onOpen = $state<VoidCallback>(noop);
    let onClose = $state<VoidCallback>(noop);
    let onOpened = $state<VoidCallback>(noop);
    let onClosed = $state<VoidCallback>(noop);

    const open: Open = (NewComponent, newProps = {}, options = {}, callback = {}) => {
        ActiveComponent = NewComponent;
        componentProps = newProps;
        cmpState = { ...defaultState, ...options };
        onOpen = callback.onOpen || noop;
        onClose = callback.onClose || noop;
        onOpened = callback.onOpened || noop;
        onClosed = callback.onClosed || noop;

        onOpen();
        onOpened();
    };

    const close: Close = (callback = {}) => {
        onClose = callback.onClose || onClose;
        onClosed = callback.onClosed || onClosed;
        ActiveComponent = null;
        componentProps = null;

        onClose();
        onClosed();
    };

    setModalContext({ open, close });

    function handleKeydown(event: KeyboardEvent) {
        if (!ActiveComponent) return;

        if (event.key === 'Escape' && cmpState.closeOnEsc) {
            event.preventDefault();
            close();
        }

        if (event.key === 'Tab' && modalWindow) {
            event.preventDefault();
            trapFocusWithin(modalWindow, event);
        }
    }

    function camelCaseToDash(str: string) {
        return str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-').toLowerCase();
    }

    function toCssString(props: Record<string, string | number>) {
        return Object.keys(props).reduce(
            (str, key) => `${str}; ${camelCaseToDash(key)}: ${props[key]}`,
            '',
        );
    }

    function trapFocusWithin(element: HTMLElement, event: KeyboardEvent) {
        const root = element.getRootNode();
        const activeElement =
            root instanceof ShadowRoot ? root.activeElement : document.activeElement;

        const nodes = element.querySelectorAll<HTMLElement>('*');
        const tabbable = Array.from(nodes).filter(node => node.tabIndex >= 0);

        const index = tabbable.indexOf(activeElement as HTMLElement);
        const indexToFocusNextCapped = getNextIndexToFocus(index, tabbable.length, event.shiftKey);

        tabbable[indexToFocusNextCapped].focus();
    }

    function getNextIndexToFocus(
        indexOfActiveElement: number,
        numOfElements: number,
        withShiftKey: boolean,
    ): number {
        const indexToFocusNext =
            indexOfActiveElement === -1 && withShiftKey
                ? -1
                : indexOfActiveElement === -1
                  ? 0
                  : indexOfActiveElement + (withShiftKey ? -1 : 1);

        return (indexToFocusNext + numOfElements) % numOfElements;
    }
</script>

<svelte:window on:keydown={handleKeydown} />

<slot />
{#if ActiveComponent}
    <div
        class="pop-over"
        role="dialog"
        aria-modal="true"
        bind:this={modalWindow}
        use:draggable={'.header'}
        use:embedInParent
        style={cssWindow}
    >
        <div class="header">
            <div class="header__title">{cmpState.title}</div>
            <button
                onclick={() => close()}
                class="header__close-button"
                data-testid="header-close"
                style={cssCloseButton}
            >
                <i class="material-icons">close</i>
            </button>
        </div>
        <div class="content" style={cssContent}>
            {#if ActiveComponent}
                <svelte:component this={ActiveComponent} {...componentProps} />
            {/if}
        </div>
    </div>
{/if}

<style lang="scss">
    @use '../../../../common/styles/mixins.scss' as m;

    .pop-over {
        position: fixed;
        top: 2rem;
        left: 2rem;
        z-index: 10;

        min-width: 300px;
        min-height: 100px;
        max-width: 100%;
        max-height: 90%;

        margin: 2rem;
        color: black;
        border-radius: 0.5rem;
        border: 1px solid black;
        background: white;
        background-color: white;

        resize: both;
        overflow: auto;
    }

    .header {
        cursor: move;
        cursor: grab;

        display: flex;

        padding: 1rem;
        padding-bottom: 0;

        &:active {
            cursor: grabbing;
        }

        &__title {
            flex-grow: 1;
            user-select: none;
        }

        &__close-button {
            @include m.reset-button;

            width: 1.5rem;
            height: 1.5rem;

            background: white;
        }
    }

    .content {
        position: relative;
        padding: 1rem;
        overflow: auto;
    }
</style>
